import { delaiEnMois } from './contrat';

/**
 * Le licenciement selon le Code des obligations : temps d'essai (art. 335b),
 * délai de congé (art. 335c) et protection pendant la maladie ou l'accident
 * (art. 336c). Les dates sont des chaînes AAAA-MM-JJ.
 */

const iso = (d) =>
  d.getFullYear() +
  '-' +
  String(d.getMonth() + 1).padStart(2, '0') +
  '-' +
  String(d.getDate()).padStart(2, '0');
const date = (s) =>
  /^\d{4}-\d{2}-\d{2}/.test(String(s || ''))
    ? new Date(String(s).slice(0, 10) + 'T00:00:00')
    : null;
const plusJours = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const finDuMois = (d, moisApres) => new Date(d.getFullYear(), d.getMonth() + moisApres + 1, 0);

/**
 * @param {object} emp fiche (dateEmbauche, essai « 2 mois »)
 * @returns {?string} le dernier jour du temps d'essai ; un mois sans précision (art. 335b)
 */
export function finDuTempsDEssai(emp) {
  const entree = date(emp?.dateEmbauche);
  if (!entree) return null;
  const s = String(emp?.essai || '').toLowerCase();
  if (/aucun|pas d|sans/.test(s)) return null;
  const m = s.match(/(\d+)/);
  const mois = m ? Math.min(+m[1], 3) : 1;
  return iso(new Date(entree.getFullYear(), entree.getMonth() + mois, entree.getDate() - 1));
}

/**
 * Jours de protection pendant une maladie ou un accident (art. 336c al. 1 let. b) :
 * 30 jours la première année de service, 90 de la deuxième à la cinquième, 180 ensuite.
 *
 * @param {object} emp fiche
 * @param {string} le jour considéré
 * @returns {number} les jours
 */
export function joursDeProtection(emp, le) {
  const entree = date(emp?.dateEmbauche);
  const jour = date(le);
  if (!entree || !jour) return 30;
  const ans = (jour - entree) / (365.25 * 86400000);
  if (ans < 1) return 30;
  return ans < 5 ? 90 : 180;
}

/**
 * Le dernier jour proposé pour un congé donné le jour `le`.
 *
 * @param {object} emp fiche
 * @param {string} le jour où la lettre est remise
 * @returns {{dernierJour: string, raison: string, mois: number, essai: boolean}} la proposition
 */
export function dernierJourLegal(emp, le) {
  const jour = date(le) || new Date();
  const finEssai = finDuTempsDEssai(emp);
  if (finEssai && iso(jour) <= finEssai) {
    return { dernierJour: iso(plusJours(jour, 7)), raison: 'essai', mois: 0, essai: true };
  }
  const mois = delaiEnMois(emp, jour);
  return { dernierJour: iso(finDuMois(jour, mois)), raison: 'delai', mois, essai: false };
}

const estMaladie = (c) => /maladie|accident/i.test(String(c?.type || '')) && c?.statut !== 'Refusé';

/**
 * Vérifie la protection de l'art. 336c pour un licenciement.
 *
 * <p>Une lettre remise pendant une maladie ou un accident, dans la période de
 * protection, est nulle. Une maladie qui survient pendant le délai de congé le
 * suspend : la fin est repoussée d'autant, puis à la fin du mois suivant.
 *
 * @param {object} emp fiche
 * @param {object[]} conges absences (empId, debut, fin, type, statut)
 * @param {string} le jour où la lettre est remise
 * @param {string} dernierJour fin du délai de congé prévue
 * @returns {{nul: ?object, suspensions: object[], joursSuspendus: number, nouveauDernierJour: ?string}}
 */
export function verifieProtection(emp, conges, le, dernierJour) {
  const vide = { nul: null, suspensions: [], joursSuspendus: 0, nouveauDernierJour: null };
  const jour = date(le);
  const fin = date(dernierJour);
  if (!jour || !emp) return vide;
  // Pendant le temps d'essai, la protection ne s'applique pas.
  const finEssai = finDuTempsDEssai(emp);
  if (finEssai && iso(jour) <= finEssai) return vide;
  const maladies = (conges || [])
    .filter((c) => c.empId === emp.id && estMaladie(c) && date(c.debut))
    .map((c) => ({ ...c, d: date(c.debut), f: date(c.fin) || date(c.debut) }));
  const protection = joursDeProtection(emp, le);
  const nul =
    maladies.find((c) => c.d <= jour && jour <= c.f && (jour - c.d) / 86400000 < protection) ||
    null;
  const suspensions = [];
  let joursSuspendus = 0;
  if (fin) {
    for (const c of maladies) {
      const debut = c.d > jour ? c.d : plusJours(jour, 1);
      const finS = c.f < fin ? c.f : fin;
      if (debut <= finS) {
        const n = Math.min(Math.round((finS - debut) / 86400000) + 1, protection);
        suspensions.push({ ...c, jours: n });
        joursSuspendus += n;
      }
    }
  }
  joursSuspendus = Math.min(joursSuspendus, protection);
  const nouveauDernierJour =
    joursSuspendus > 0 && fin ? iso(finDuMois(plusJours(fin, joursSuspendus), 0)) : null;
  return { nul, suspensions, joursSuspendus, nouveauDernierJour };
}
